import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Bot, Send, X, Minimize2, Maximize2, Image, Settings, Star, TrendingUp, 
  BookOpen, Target, Clock, Sparkles, GraduationCap, BarChart3, Users,
  DollarSign, Shield, Activity, Lightbulb, FileText, Calendar, Award,
  MessageCircle, Mic, Paperclip, RefreshCw, ChevronDown, Zap, Brain,
  PieChart, LineChart, Briefcase, School, Trophy, Heart, Volume2
} from 'lucide-react';
import axios from 'axios';
import { MessageFormatter } from './MessageFormatter';
import './MessageDisplay.css';
import { useAuthStore } from '../../stores/authStore';
import UserAvatar from '../../components/UserAvatar';

// 消息接口
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  actions?: any[];
  imageUrl?: string;
  type?: 'text' | 'card' | 'chart' | 'recommendation';
  metadata?: any;
}

// 用户角色类型
type UserRole = 'student' | 'teacher' | 'admin';

// 角色配置
interface RoleConfig {
  name: string;
  title: string;
  subtitle: string;
  gradient: string;
  accentColor: string;
  icon: React.ElementType;
  welcomeEmoji: string;
  features: string[];
  quickActions: { label: string; icon: React.ElementType; action: string }[];
  suggestions: string[];
}

const roleConfigs: Record<UserRole, RoleConfig> = {
  student: {
    name: '小智学习助手',
    title: 'AI 学习伙伴',
    subtitle: '陪伴你的每一步成长',
    gradient: 'from-blue-500 via-indigo-500 to-purple-600',
    accentColor: 'indigo',
    icon: GraduationCap,
    welcomeEmoji: '📚',
    features: ['智能答疑', '学习规划', '考试辅导', '课程推荐', '作业帮助', '知识图谱'],
    quickActions: [
      { label: '学习计划', icon: Calendar, action: 'learning_plan' },
      { label: '课程推荐', icon: BookOpen, action: 'course_recommend' },
      { label: '考试辅导', icon: FileText, action: 'exam_help' },
      { label: '问题答疑', icon: Lightbulb, action: 'qa' }
    ],
    suggestions: [
      '📚 帮我制定学习计划',
      '🎯 推荐适合我的课程',
      '📝 我要做考试练习',
      '💡 帮我解答学习问题',
      '⏰ 优化我的学习时间',
      '🏆 查看我的学习进度'
    ]
  },
  teacher: {
    name: '智能教学助手',
    title: 'AI 教学顾问',
    subtitle: '提升教学效率与收入',
    gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
    accentColor: 'emerald',
    icon: Briefcase,
    welcomeEmoji: '📊',
    features: ['市场分析', '课程优化', '学生管理', '定价策略', '竞争分析', '收入预测'],
    quickActions: [
      { label: '市场分析', icon: TrendingUp, action: 'market_analysis' },
      { label: '课程建议', icon: Lightbulb, action: 'course_advice' },
      { label: '学生管理', icon: Users, action: 'student_manage' },
      { label: '收入优化', icon: DollarSign, action: 'revenue_optimize' }
    ],
    suggestions: [
      '📊 分析当前市场趋势',
      '💰 优化我的课程定价',
      '👥 分析我的学生数据',
      '🎯 推荐热门课程方向',
      '📈 查看我的教学表现',
      '🏅 与其他老师对比分析'
    ]
  },
  admin: {
    name: '平台管理助手',
    title: 'AI 运营顾问',
    subtitle: '数据驱动平台增长',
    gradient: 'from-slate-700 via-slate-800 to-slate-900',
    accentColor: 'slate',
    icon: Shield,
    welcomeEmoji: '🛡️',
    features: ['平台分析', '用户洞察', '风险预警', '运营建议', '收入分析', '系统优化'],
    quickActions: [
      { label: '平台概览', icon: BarChart3, action: 'platform_overview' },
      { label: '用户分析', icon: Users, action: 'user_analysis' },
      { label: '风险预警', icon: Shield, action: 'risk_alert' },
      { label: '运营建议', icon: Target, action: 'operation_advice' }
    ],
    suggestions: [
      '📊 查看平台整体数据',
      '👥 分析用户增长趋势',
      '⚠️ 检查风险预警',
      '💡 获取运营优化建议',
      '💰 分析收入构成',
      '🔧 系统性能报告'
    ]
  }
};

interface RoleBasedAIChatbotProps {
  isOpen?: boolean;
  onClose?: () => void;
  className?: string;
}

export const RoleBasedAIChatbot: React.FC<RoleBasedAIChatbotProps> = ({
  isOpen = false,
  onClose,
  className = ""
}) => {
  const { user } = useAuthStore();
  const userRole = (user?.role as UserRole) || 'student';
  const config = roleConfigs[userRole];
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      initializeChat();
    }
  }, [isOpen, userRole]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeChat = () => {
    const currentHour = new Date().getHours();
    let greeting = currentHour < 12 ? '早上好' : currentHour < 18 ? '下午好' : '晚上好';
    const userName = user?.firstName || '';

    const welcomeContent = generateWelcomeMessage(greeting, userName);
    
    const welcomeMessage: Message = {
      id: Date.now().toString(),
      role: 'assistant',
      content: welcomeContent,
      timestamp: new Date(),
      suggestions: config.suggestions.slice(0, 6)
    };
    
    setMessages([welcomeMessage]);
  };

  const generateWelcomeMessage = (greeting: string, userName: string): string => {
    const nameGreeting = userName ? `${userName}同学` : '';
    
    switch (userRole) {
      case 'student':
        return `${config.welcomeEmoji} ${greeting}${nameGreeting ? `，${nameGreeting}` : ''}！我是你的 ${config.name}～

✨ **我能帮你做什么：**
• 📖 制定个性化学习计划
• 🎯 智能推荐适合你的课程
• 📝 考试练习与智能出题
• 💡 解答学习中的疑问
• ⏰ 优化学习时间安排
• 📊 追踪学习进度与成绩

💬 有什么我能帮到你的吗？`;

      case 'teacher':
        return `${config.welcomeEmoji} ${greeting}${userName ? `，${userName}老师` : ''}！我是您的 ${config.name}。

🎯 **专为教师打造的 AI 服务：**
• 📊 实时市场趋势分析
• 💰 智能课程定价建议
• 👥 学生学习数据分析
• 🏆 竞争对手深度洞察
• 📈 教学效果评估报告
• 🎯 课程内容优化建议

💼 今天想了解什么？`;

      case 'admin':
        return `${config.welcomeEmoji} ${greeting}${userName ? `，${userName}` : '管理员'}！我是平台 ${config.name}。

🛡️ **平台管理智能助手：**
• 📊 平台核心数据概览
• 👥 用户增长与行为分析
• ⚠️ 风险预警与安全监控
• 💡 运营策略智能建议
• 💰 收入趋势与预测分析
• 🔧 系统性能优化建议

📈 需要了解哪方面的数据？`;

      default:
        return `${greeting}！我是你的 AI 助手，有什么可以帮到你的吗？`;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setShowQuickActions(false);

    try {
      const response = await axios.post('/api/ai/chat', {
        message: content,
        sessionId: sessionId,
        userRole: userRole,
        context: {
          role: userRole,
          userName: user?.firstName,
          chatHistory: messages.slice(-5)
        }
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const { data } = response.data;
      
      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        suggestions: data.suggestions || generateContextualSuggestions(content),
        actions: data.actions,
        type: data.type || 'text',
        metadata: data.metadata
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('发送消息失败:', error);
      const errorMessage = getErrorMessage(error);
      
      const errorResponseMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date(),
        suggestions: ['重新发送', '联系客服', '查看帮助']
      };

      setMessages(prev => [...prev, errorResponseMessage]);
    } finally {
      setLoading(false);
    }
  };

  const generateContextualSuggestions = (lastMessage: string): string[] => {
    // 根据用户角色和最后消息生成上下文相关建议
    const baseSuggestions = config.suggestions;
    return baseSuggestions.slice(0, 4);
  };

  const getErrorMessage = (error: any): string => {
    if (error.response?.status === 401) {
      return '😅 您需要先登录才能使用 AI 助手功能哦～';
    } else if (error.response?.status === 429) {
      return '🙏 您的提问太积极了！请稍等片刻再试～';
    } else if (error.response?.status >= 500) {
      return '😓 AI 助手暂时开小差了，请稍后再试～';
    }
    return '😔 抱歉，我现在无法回复您的消息，请稍后重试。';
  };

  const handleQuickAction = (action: string) => {
    const actionMessages: Record<string, string> = {
      learning_plan: '帮我制定一个个性化的学习计划',
      course_recommend: '根据我的情况推荐适合的课程',
      exam_help: '我需要进行考试练习',
      qa: '我有一个学习问题想请教',
      market_analysis: '分析一下当前教育市场的趋势',
      course_advice: '给我一些课程内容优化的建议',
      student_manage: '帮我分析学生的学习情况',
      revenue_optimize: '如何优化我的课程收入',
      platform_overview: '展示平台整体运营数据',
      user_analysis: '分析用户增长和活跃度数据',
      risk_alert: '检查当前有哪些风险预警',
      operation_advice: '给出平台运营优化建议'
    };
    
    sendMessage(actionMessages[action] || action);
  };

  const handleSuggestionClick = (suggestion: string) => {
    // 移除 emoji 前缀
    const cleanSuggestion = suggestion.replace(/^[^\s]+ /, '');
    sendMessage(cleanSuggestion);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputMessage);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowQuickActions(true);
    initializeChat();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setImagePreview(result);
        setUploadedImage(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const ensureAbsoluteAvatarUrl = (avatarUrl: string | undefined): string => {
    if (!avatarUrl) return '';
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl;
    }
    return `${window.location.origin}${avatarUrl}`;
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed z-50 ${className}`}
      style={{
        bottom: 'max(env(safe-area-inset-bottom, 0px) + 16px, 16px)',
        right: 'max(env(safe-area-inset-right, 0px) + 16px, 16px)',
        left: isMinimized ? 'auto' : 'max(env(safe-area-inset-left, 0px) + 16px, 16px)',
      }}
    >
      <div className={`bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/60 ${
        isMinimized 
          ? 'w-72 sm:w-80 h-16 ml-auto' 
          : 'w-full sm:w-[440px] sm:max-w-[92vw] h-[calc(100vh-100px)] sm:h-[680px] sm:max-h-[90vh] sm:ml-auto'
      } transition-all duration-300 flex flex-col overflow-hidden`}>
        
        {/* 头部 - 角色定制 */}
        <div className={`flex items-center justify-between p-4 sm:p-5 bg-gradient-to-r ${config.gradient} text-white`}>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                <config.icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-base sm:text-lg truncate">{config.title}</h3>
              <p className="text-xs sm:text-sm text-white/80 truncate">{config.subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-white/20 rounded-xl transition-all"
              title={isMinimized ? '展开' : '最小化'}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-xl transition-all"
                title="关闭"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* 快捷操作栏 */}
            {showQuickActions && messages.length <= 1 && (
              <div className="px-4 sm:px-5 py-3 sm:py-4 bg-slate-50/80 border-b border-slate-100">
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span className="text-xs sm:text-sm font-semibold text-slate-700">快捷功能</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {config.quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickAction(action.action)}
                      className={`flex flex-col items-center gap-1.5 sm:gap-2 p-2.5 sm:p-3 rounded-xl border-2 border-slate-100 bg-white hover:border-${config.accentColor}-300 hover:bg-${config.accentColor}-50/50 transition-all group`}
                    >
                      <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform`}>
                        <action.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <span className="text-[10px] sm:text-xs font-medium text-slate-700 group-hover:text-slate-900">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 消息区域 */}
            <div className={`flex-1 p-4 sm:p-5 overflow-y-auto space-y-4 sm:space-y-5 bg-gradient-to-br from-slate-50/50 via-white to-${config.accentColor}-50/30`}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} items-start group`}
                >
                  {/* AI头像 */}
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 mr-2 sm:mr-3">
                      <div className={`w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br ${config.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                        <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                    </div>
                  )}
                  
                  <div
                    className={`max-w-[85%] min-w-[180px] sm:min-w-[200px] ${
                      message.role === 'user'
                        ? `bg-gradient-to-br ${config.gradient} text-white shadow-lg shadow-${config.accentColor}-200/50`
                        : 'bg-white shadow-md border border-slate-100'
                    } rounded-2xl ${
                      message.role === 'user' 
                        ? 'rounded-br-md' 
                        : 'rounded-bl-md'
                    } overflow-hidden group-hover:shadow-xl transition-all duration-300`}
                  >
                    <div className="p-4 sm:p-5">
                      {/* 图片预览 */}
                      {message.imageUrl && (
                        <div className="mb-3 sm:mb-4">
                          <img 
                            src={message.imageUrl} 
                            alt="上传的图片" 
                            className="max-w-full h-auto rounded-xl max-h-40 sm:max-h-48 object-cover shadow-sm"
                          />
                        </div>
                      )}
                      
                      {/* 消息文本 */}
                      <div className={`ai-message-container ${
                        message.role === 'user' ? 'text-white' : 'text-slate-800'
                      }`}>
                        <MessageFormatter
                          content={message.content}
                          isUser={message.role === 'user'}
                          className={message.role === 'user' ? 'text-white' : ''}
                        />
                      </div>
                      
                      {/* 智能建议 */}
                      {message.suggestions && message.suggestions.length > 0 && (
                        <div className="mt-4 sm:mt-5 space-y-2 sm:space-y-3">
                          <div className={`text-xs font-semibold flex items-center gap-1.5 ${
                            message.role === 'user' ? 'text-white/80' : 'text-slate-600'
                          }`}>
                            <Sparkles className="w-3.5 h-3.5" />
                            快捷回复
                          </div>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {message.suggestions.slice(0, 4).map((suggestion, index) => (
                              <button
                                key={index}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs font-medium transition-all duration-200 ${
                                  message.role === 'user'
                                    ? 'bg-white/15 hover:bg-white/25 text-white border border-white/20'
                                    : `bg-${config.accentColor}-50 hover:bg-${config.accentColor}-100 text-${config.accentColor}-700 border border-${config.accentColor}-200`
                                }`}
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* 时间戳 */}
                    <div className={`px-4 sm:px-5 py-2 text-[10px] sm:text-xs flex items-center gap-1.5 ${
                      message.role === 'user' 
                        ? 'text-white/60 bg-black/5' 
                        : 'text-slate-400 bg-slate-50/50'
                    }`}>
                      <Clock className="w-3 h-3" />
                      {message.timestamp.toLocaleTimeString('zh-CN', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                  
                  {/* 用户头像 */}
                  {message.role === 'user' && (
                    <div className="ml-2 sm:ml-3 flex-shrink-0">
                      <UserAvatar
                        avatar={ensureAbsoluteAvatarUrl(user?.avatar)}
                        firstName={user?.firstName}
                        lastName={user?.lastName}
                        size="sm"
                        className={`bg-gradient-to-br ${config.gradient}`}
                      />
                    </div>
                  )}
                </div>
              ))}
              
              {/* 加载动画 */}
              {loading && (
                <div className="flex justify-start items-start">
                  <div className="flex-shrink-0 mr-2 sm:mr-3">
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br ${config.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                      <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-pulse" />
                    </div>
                  </div>
                  
                  <div className="bg-white shadow-md border border-slate-100 rounded-2xl rounded-bl-md overflow-hidden">
                    <div className="p-4 sm:p-5">
                      <div className="flex items-center space-x-3">
                        <div className="flex space-x-1.5">
                          <div className={`w-2 h-2 bg-${config.accentColor}-500 rounded-full animate-bounce`} />
                          <div className={`w-2 h-2 bg-${config.accentColor}-500 rounded-full animate-bounce`} style={{ animationDelay: '0.15s' }} />
                          <div className={`w-2 h-2 bg-${config.accentColor}-500 rounded-full animate-bounce`} style={{ animationDelay: '0.3s' }} />
                        </div>
                        <span className="text-sm text-slate-500 font-medium">AI 正在思考...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* 图片预览 */}
            {imagePreview && (
              <div className="p-3 sm:p-4 border-t border-slate-100 bg-slate-50/80">
                <div className="relative inline-block">
                  <img src={imagePreview} alt="预览" className="max-w-full h-24 sm:h-32 object-cover rounded-xl shadow-sm" />
                  <button
                    onClick={() => { setImagePreview(null); setUploadedImage(null); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600 shadow-lg transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* 输入区域 */}
            <div className="p-3 sm:p-4 border-t border-slate-100 bg-white">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="输入您的问题..."
                    disabled={loading}
                    className={`w-full px-4 sm:px-5 py-2.5 sm:py-3 bg-slate-50 border-2 border-slate-100 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-${config.accentColor}-500/30 focus:border-${config.accentColor}-300 disabled:opacity-50 pr-12 sm:pr-14 text-sm sm:text-base transition-all`}
                  />
                  <div className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                      title="上传图片"
                    >
                      <Image className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => sendMessage(inputMessage)}
                  disabled={loading || !inputMessage.trim()}
                  className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${config.gradient} text-white rounded-xl sm:rounded-2xl flex items-center justify-center hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed transition-all shadow-md`}
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
              
              {/* 底部操作栏 */}
              <div className="flex items-center justify-between mt-2 sm:mt-3 text-[10px] sm:text-xs">
                <button
                  onClick={clearChat}
                  className="flex items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <RefreshCw className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  重新对话
                </button>
                <div className={`flex items-center gap-1.5 text-${config.accentColor}-500`}>
                  <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="font-medium">{config.name}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// 角色定制触发按钮
export const RoleBasedChatbotTrigger: React.FC<{ onClick: () => void; className?: string }> = ({ onClick, className = '' }) => {
  const { user } = useAuthStore();
  const userRole = (user?.role as UserRole) || 'student';
  const config = roleConfigs[userRole];

  return (
    <button
      onClick={onClick}
      className={`group fixed z-40 ${className}`}
      style={{ 
        bottom: 'max(env(safe-area-inset-bottom, 0px) + 80px, 80px)',
        right: 'max(env(safe-area-inset-right, 0px) + 16px, 16px)'
      }}
    >
      {/* 脉冲动画背景 */}
      <div className={`absolute inset-0 bg-gradient-to-r ${config.gradient} rounded-xl sm:rounded-2xl animate-ping opacity-20`} />
      
      {/* 主按钮 */}
      <div className={`relative flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-4 bg-gradient-to-r ${config.gradient} text-white rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 group-hover:scale-105`}>
        {/* AI 图标 */}
        <div className="relative">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center">
            <config.icon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
        </div>
        
        {/* 文字 */}
        <div className="pr-0 sm:pr-1">
          <div className="text-xs sm:text-sm font-bold">{config.title}</div>
          <div className="hidden sm:block text-[10px] sm:text-xs text-white/80">点击开始对话</div>
        </div>
        
        {/* 图标 */}
        <MessageCircle className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 opacity-80 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </button>
  );
};

export default RoleBasedAIChatbot;

